/**
 * screens/components/BranchVcsPanel.js
 *
 * The git-style control surface on the Version History screen (own history
 * only).  Surfaces the analyst's working branch as:
 *
 *   • a header row with the branch name, commit count, and actions:
 *       Commit (snapshot now, with a message) · Push (to cloud) ·
 *       Pull (another analyst's branch in for merging)
 *   • a commit log — each commit shows its message, time, and +/- diff summary,
 *       with a "Restore" action that opens the review dialog in restore mode
 *   • any pulled branches, each with a "Merge" action (review dialog, merge mode)
 *
 * All mutating actions funnel through the VCS managers and then `refresh()`.
 * A successful merge/restore bubbles the merged string up via `onMerged` so the
 * parent can push it into Redux (the editor continues from the merged state).
 */

import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Badge,
  Box,
  Button,
  Chip,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Divider,
} from "@mui/material";
import {
  AccountTree,
  Save,
  CloudUpload,
  CloudDownload,
  CallMerge,
  Restore,
  Sync,
  Search,
  Visibility,
  CompareArrows,
} from "@mui/icons-material";
import moment from "moment";
import toast from "react-hot-toast";

import { useVcs } from "../../persistence/hooks/useVcs";
import { commit as commitBranch, defaultBranchName, checkout } from "../../persistence/vcs/BranchManager";
import { flush as flushBridge } from "../../persistence/vcs/StringBridge";
import { previewMerge } from "../../persistence/vcs/MergeManager";
import { pushBranch, pullBranch } from "../../persistence/vcs/vcsFirestore";
import { parseEventTokens, computeDiff } from "../../persistence/managers/DiffManager";
import { loadDoc, docToTokens } from "../../persistence/vcs/yjsDoc";
import MergeReviewDialog from "./MergeReviewDialog";

const BranchVcsPanel = ({
  matchId,
  analystId,
  analysts = [],
  onMerged,
  // Read-only mode (e.g. the management portal): commits are supplied from the
  // cloud, no local data is loaded, and every mutating action is hidden — only
  // viewing + Preview remain.
  readOnly = false,
  commits: externalCommits,
  loading: externalLoading = false,
  title,
  // Latest match string from the backend.  When provided, the commit whose
  // string matches it is marked "latest" (instead of simply the newest commit).
  serverString = null,
}) => {
  const {
    commits: localCommits,
    branches,
    loading: localLoading,
    syncing,
    refresh,
    syncMyWork,
  } = useVcs({ matchId, analystId, enabled: !readOnly });

  const commits = readOnly ? externalCommits ?? [] : localCommits;
  const loading = readOnly ? Boolean(externalLoading) : localLoading;

  // Live working string from Redux — shown as a "Current String" row (own view
  // only) so the analyst can view their latest in-progress state alongside saved
  // versions, even before it's been committed.
  const reduxString = useSelector((s) => s.control?.analysedString?.[matchId] ?? "");
  // Prefer the live in-editor string; fall back to the fetched backend string so
  // the "Current String" row still shows when opening History directly.
  const currentString = reduxString || serverString || "";

  const ownBranchName = defaultBranchName(analystId);
  const pulledBranches = readOnly ? [] : branches.filter((b) => b.branchName !== ownBranchName);
  const otherAnalysts = analysts.filter((a) => String(a.id) !== String(analystId));

  // Stable key/identity that works for both local commits (commitId) and cloud
  // commits from useFirestoreCommitHistory (id).
  const commitKey = (c) => c.commitId ?? c.id;

  // Token signature of a commit's string — cloud commits carry `.string`,
  // local commits decode from their Yjs `stateUpdate`.
  const commitTokenSig = useCallback((c) => {
    try {
      if (c.string != null) return parseEventTokens(c.string).join(",");
      if (c.stateUpdate) return docToTokens(loadDoc(c.stateUpdate)).join(",");
    } catch { /* ignore decode failures */ }
    return null;
  }, []);

  // "Latest" = the commit whose string matches the live backend string.  Until
  // serverString is fetched (null), fall back to the newest commit.  If none
  // match, no commit is marked latest.
  const latestCommitKey = useMemo(() => {
    if (serverString == null) return commits[0] ? commitKey(commits[0]) : null;
    const target = parseEventTokens(serverString).join(",");
    const match = commits.find((c) => commitTokenSig(c) === target);
    return match ? commitKey(match) : null;
  }, [commits, serverString, commitTokenSig]); // eslint-disable-line react-hooks/exhaustive-deps

  // Friendly, jargon-free label for a branch: own → "My work", a pulled
  // teammate branch ("pull/{ownerId}/…") → "{Name}'s work".
  const friendlyBranchLabel = useCallback(
    (branchName) => {
      if (branchName === ownBranchName) return "My work";
      const m = branchName.match(/^pull\/([^/]+)\//);
      if (m) {
        const owner = analysts.find((a) => String(a.id) === m[1]);
        const name = owner?.name ?? owner?.username ?? `Analyst ${m[1]}`;
        return `${name}'s work`;
      }
      return branchName;
    },
    [ownBranchName, analysts]
  );

  // ── Action state ────────────────────────────────────────────────────────────
  const [busy, setBusy] = useState(""); // 'commit' | 'push' | 'pull'
  const [commitOpen, setCommitOpen] = useState(false);
  const [commitMsg, setCommitMsg] = useState("");
  const [pullAnchor, setPullAnchor] = useState(null);
  const [preview, setPreview] = useState(null);
  const [search, setSearch] = useState("");
  const [previewCommit, setPreviewCommit] = useState(null); // { commit, string }
  const [compareKeys, setCompareKeys] = useState([]); // up to 2 selected commitKeys
  const [diffResult, setDiffResult] = useState(null);  // { base, target, diff }

  // Filter the commit log by message / version number for quick find.
  const filteredCommits = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return commits;
    return commits.filter(
      (c) =>
        (c.message ?? "").toLowerCase().includes(q) ||
        `v${c.versionNumber ?? ""}`.toLowerCase().includes(q) ||
        moment(c.createdAt).format("DD MMM HH:mm").toLowerCase().includes(q)
    );
  }, [commits, search]);

  const openPreview = useCallback(async (commit) => {
    try {
      // Read-only (cloud) commits already carry the decoded string; local
      // commits are read back from IndexedDB via checkout().
      const string = readOnly
        ? commit.string ?? ""
        : await checkout(commit.commitId);
      setPreviewCommit({ commit, string });
    } catch (err) {
      toast.error("Could not load version: " + err.message);
    }
  }, [readOnly]);

  // ── Compare two commits ─────────────────────────────────────────────────────
  // Resolve a commit's string: cloud commits carry `.string`, local ones decode
  // from IndexedDB via checkout().
  const resolveString = useCallback(
    async (c) => (readOnly ? c.string ?? "" : await checkout(c.commitId)),
    [readOnly]
  );

  const toggleCompare = useCallback((c) => {
    const k = commitKey(c);
    setCompareKeys((prev) => {
      if (prev.includes(k)) return prev.filter((x) => x !== k);
      if (prev.length >= 2) return prev; // already have 2 — ignore extra picks
      return [...prev, k];
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const clearCompare = useCallback(() => {
    setCompareKeys([]);
    setDiffResult(null);
  }, []);

  const runCompare = useCallback(async (keys) => {
    try {
      const picked = keys
        .map((k) => commits.find((c) => commitKey(c) === k))
        .filter(Boolean);
      if (picked.length !== 2) return;
      // Older commit = base, newer = target (by versionNumber then createdAt).
      const [base, target] = picked.sort(
        (a, b) =>
          (a.versionNumber ?? 0) - (b.versionNumber ?? 0) ||
          (a.createdAt ?? 0) - (b.createdAt ?? 0)
      );
      const [baseStr, targetStr] = await Promise.all([
        resolveString(base),
        resolveString(target),
      ]);
      setDiffResult({ base, target, diff: computeDiff(baseStr, targetStr) });
    } catch (err) {
      toast.error("Could not compute diff: " + err.message);
    }
  }, [commits, resolveString]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-open the diff as soon as two versions are selected.
  useEffect(() => {
    if (compareKeys.length === 2) runCompare(compareKeys);
  }, [compareKeys, runCompare]);

  const openCurrentPreview = useCallback(() => {
    setPreviewCommit({
      commit: {
        isCurrent: true,
        message: "Current String",
        createdAt: Date.now(),
        eventCount: parseEventTokens(currentString).length,
      },
      string: currentString,
    });
  }, [currentString]);

  // ── Commit ────────────────────────────────────────────────────────────────
  const handleCommit = useCallback(async () => {
    setBusy("commit");
    try {
      await flushBridge(matchId, analystId); // persist any in-flight edits first
      const c = await commitBranch({
        matchId,
        analystId,
        message: commitMsg.trim() || "Manual commit",
      });
      if (c) {
        toast.success("Version saved ✓", { duration: 2500 });
        setCommitOpen(false);
        setCommitMsg("");
        refresh();
      } else {
        toast.error("Nothing to save");
      }
    } catch (err) {
      toast.error("Save failed: " + err.message);
    } finally {
      setBusy("");
    }
  }, [matchId, analystId, commitMsg, refresh]);

  // ── Push ──────────────────────────────────────────────────────────────────
  const handlePush = useCallback(async () => {
    setBusy("push");
    try {
      const { commitsPushed } = await pushBranch({ matchId, analystId });
      toast.success(`Shared · ${commitsPushed} new version${commitsPushed !== 1 ? "s" : ""} ☁️`, {
        duration: 3000,
      });
      refresh();
    } catch (err) {
      toast.error("Share failed: " + err.message);
    } finally {
      setBusy("");
    }
  }, [matchId, analystId, refresh]);

  // ── Sync my work (fetch own branch from cloud) ─────────────────────────────
  const handleSyncMyWork = useCallback(async () => {
    const res = await syncMyWork();
    if (!res.fetched) {
      toast("Nothing shared from another device yet", { duration: 2500 });
    } else if (res.newCommits > 0) {
      toast.success(`Synced · ${res.newCommits} new version${res.newCommits !== 1 ? "s" : ""} from cloud`, { duration: 3000 });
    } else {
      toast.success("Up to date with cloud", { duration: 2500 });
    }
  }, [syncMyWork]);

  // ── Pull ──────────────────────────────────────────────────────────────────
  const handlePull = useCallback(
    async (owner) => {
      setPullAnchor(null);
      setBusy("pull");
      try {
        const created = await pullBranch({
          matchId,
          analystId,
          ownerAnalystId: String(owner.id),
          branchName: defaultBranchName(String(owner.id)),
        });
        if (created) {
          toast.success(`Pulled ${owner.name ?? owner.username ?? owner.id}'s branch`, { duration: 3000 });
          refresh();
        }
      } catch (err) {
        toast.error("Pull failed: " + err.message);
      } finally {
        setBusy("");
      }
    },
    [matchId, analystId, refresh]
  );

  // ── Open review dialog (restore a commit / merge a pulled branch) ──────────
  const openRestore = useCallback(
    async (commit) => {
      try {
        const p = await previewMerge({
          matchId,
          analystId,
          targetBranchName: ownBranchName,
          source: { type: "commit", commitId: commit.commitId, label: commit.message || `v` },
          mode: "restore",
        });
        setPreview(p);
      } catch (err) {
        toast.error("Could not build preview: " + err.message);
      }
    },
    [matchId, analystId, ownBranchName]
  );

  const openMerge = useCallback(
    async (srcBranch) => {
      try {
        const p = await previewMerge({
          matchId,
          analystId,
          targetBranchName: ownBranchName,
          source: { type: "branch", branchName: srcBranch.branchName, label: friendlyBranchLabel(srcBranch.branchName) },
          mode: "merge",
        });
        setPreview(p);
      } catch (err) {
        toast.error("Could not build preview: " + err.message);
      }
    },
    [matchId, analystId, ownBranchName, friendlyBranchLabel]
  );

  const handleMerged = useCallback(
    (mergedString) => {
      setPreview(null);
      refresh();
      if (onMerged) onMerged(mergedString);
    },
    [refresh, onMerged]
  );

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid var(--border-1)",
        borderRadius: 2,
        backgroundColor: "var(--surface-1)",
        mb: 2.5,
        overflow: "hidden",
      }}
    >
      {/* Header row */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems={{ xs: "stretch", sm: "center" }}
        sx={{ px: 2, py: 1.5, borderBottom: "1px solid var(--border-1)" }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
          <AccountTree sx={{ fontSize: 17, color: "var(--primary-1)" }} />
          <Typography variant="subtitle2" fontFamily="Poppins" fontWeight={700} color="var(--text-1)" noWrap>
            {readOnly ? title ?? "Work" : "My work"}
          </Typography>
          <Chip
            label={`${commits.length} version${commits.length !== 1 ? "s" : ""}`}
            size="small"
            sx={{ height: 20, fontSize: 10, fontFamily: "Poppins", color: "var(--text-1)" }}
            variant="outlined"
          />
          {readOnly && (
            <Chip
              label="View only"
              size="small"
              sx={{ height: 20, fontSize: 10, fontFamily: "Poppins", color: "var(--text-2)" }}
              variant="outlined"
            />
          )}
        </Stack>

        {!readOnly && (
        <Stack direction="row" spacing={0.75}>
          <Tooltip title="Save a named version of your current work (a checkpoint you can come back to)">
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Save sx={{ fontSize: 14 }} />}
                onClick={() => setCommitOpen(true)}
                disabled={!!busy}
                sx={vcsBtnSx}
              >
                Save Version
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Share your work to the cloud so teammates can pick it up">
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={busy === "push" ? <CircularProgress size={12} color="inherit" /> : <CloudUpload sx={{ fontSize: 14 }} />}
                onClick={handlePush}
                disabled={!!busy}
                sx={vcsBtnSx}
              >
                Share
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Fetch your own work shared from another device (merges into this device)">
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={syncing ? <CircularProgress size={12} color="inherit" /> : <Sync sx={{ fontSize: 14 }} />}
                onClick={handleSyncMyWork}
                disabled={!!busy || syncing}
                sx={vcsBtnSx}
              >
                Sync my work
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={otherAnalysts.length === 0 ? "No other analysts on this match" : "Bring a teammate's shared work in, ready to combine"}>
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={busy === "pull" ? <CircularProgress size={12} color="inherit" /> : <CloudDownload sx={{ fontSize: 14 }} />}
                onClick={(e) => setPullAnchor(e.currentTarget)}
                disabled={!!busy || otherAnalysts.length === 0}
                sx={vcsBtnSx}
              >
                Get from analyst
              </Button>
            </span>
          </Tooltip>
          <Menu anchorEl={pullAnchor} open={Boolean(pullAnchor)} onClose={() => setPullAnchor(null)}>
            {otherAnalysts.map((a) => (
              <MenuItem key={a.id} onClick={() => handlePull(a)} sx={{ fontFamily: "Poppins", fontSize: 13 }}>
                {a.name ?? a.username ?? `Analyst ${a.id}`}
              </MenuItem>
            ))}
          </Menu>
        </Stack>
        )}
      </Stack>

      {/* Pulled branches awaiting merge */}
      {!readOnly && pulledBranches.length > 0 && (
        <Box sx={{ px: 2, py: 1.25, borderBottom: "1px solid var(--border-1)" }}>
          <Typography variant="overline" sx={{ color: "var(--text-2)", fontSize: 10 }}>
            Teammates' work
          </Typography>
          <Stack spacing={0.75} mt={0.5}>
            {pulledBranches.map((b) => (
              <Stack key={b.branchName} direction="row" alignItems="center" spacing={1}>
                <CloudDownload sx={{ fontSize: 14, color: "var(--text-2)" }} />
                <Typography variant="caption" fontFamily="Poppins" color="var(--text-1)" sx={{ flex: 1 }} noWrap>
                  {friendlyBranchLabel(b.branchName)}
                </Typography>
                <Tooltip title="Combine this teammate's work into yours (you'll review the changes first)">
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<CallMerge sx={{ fontSize: 14 }} />}
                    onClick={() => openMerge(b)}
                    sx={{ textTransform: "none", fontFamily: "Poppins", fontSize: 12, py: 0.3 }}
                  >
                    Combine
                  </Button>
                </Tooltip>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}

      {/* Commit log */}
      <Box sx={{ px: 2, py: 1.25 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.75}>
          <Typography variant="overline" sx={{ color: "var(--text-2)", fontSize: 10 }}>
            Saved versions
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            {compareKeys.length > 0 && (
              <Chip
                size="small"
                icon={<CompareArrows sx={{ fontSize: "14px !important" }} />}
                label={compareKeys.length === 1 ? "Pick 1 more to compare" : "Comparing"}
                onDelete={clearCompare}
                sx={{ height: 22, fontSize: 10, fontFamily: "Poppins", color: "var(--text-1)" }}
                variant="outlined"
              />
            )}
            {commits.length > 0 && (
              <TextField
                size="small"
                variant="standard"
                placeholder="Search…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  disableUnderline: false,
                  startAdornment: <Search sx={{ fontSize: 15, color: "var(--text-2)", mr: 0.5 }} />,
                  style: { fontFamily: "Poppins", fontSize: 12 },
                }}
                sx={{ width: 160 }}
              />
            )}
          </Stack>
        </Stack>

        {/* Current (uncommitted) working string — view only */}
        {!readOnly && currentString && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ py: 0.75, mb: 0.5, borderBottom: "1px dashed var(--border-1)" }}
          >
            <Box
              sx={{
                minWidth: 30, height: 22, px: 0.5, borderRadius: 1,
                display: "flex", alignItems: "center", justifyContent: "center",
                bgcolor: "var(--surface-2, rgba(0,0,0,0.06))", flexShrink: 0,
              }}
            >
              <Typography variant="caption" fontWeight={700} sx={{ fontSize: 9, color: "var(--text-2)" }}>
                Now
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" fontFamily="Poppins" fontWeight={600} color="var(--text-1)" noWrap display="block">
                Current String
              </Typography>
              <Typography variant="caption" color="var(--text-2)" fontFamily="Poppins" sx={{ fontSize: 10 }}>
                Live working state · {parseEventTokens(currentString).length} events
              </Typography>
            </Box>
            <Tooltip title="View your current working string">
              <IconButton size="small" onClick={openCurrentPreview}>
                <Visibility sx={{ fontSize: 16, color: "var(--text-2)" }} />
              </IconButton>
            </Tooltip>
          </Stack>
        )}

        {loading ? (
          <Stack direction="row" spacing={1} alignItems="center" py={1}>
            <CircularProgress size={14} />
            <Typography variant="caption" color="var(--text-2)" fontFamily="Poppins">
              Loading…
            </Typography>
          </Stack>
        ) : commits.length === 0 ? (
          <Typography variant="caption" color="var(--text-2)" fontFamily="Poppins">
            {readOnly
              ? "No versions in the cloud for this analyst yet."
              : "No saved versions yet. Tag in InputBoard or press Save Version to snapshot now."}
          </Typography>
        ) : filteredCommits.length === 0 ? (
          <Typography variant="caption" color="var(--text-2)" fontFamily="Poppins">
            No versions match “{search}”.
          </Typography>
        ) : (
          <Stack spacing={0} divider={<Divider sx={{ borderColor: "var(--border-1)" }} />}>
            {filteredCommits.map((c) => {
              const isLatest = latestCommitKey != null && commitKey(c) === latestCommitKey;
              return (
                <Stack key={commitKey(c)} direction="row" alignItems="center" spacing={1} sx={{ py: 0.75 }}>
                  <Box
                    sx={{
                      minWidth: 30, height: 22, px: 0.5, borderRadius: 1,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      bgcolor: isLatest ? "var(--primary-1)" : "var(--surface-2, rgba(0,0,0,0.06))",
                      flexShrink: 0,
                    }}
                  >
                    <Typography variant="caption" fontWeight={700} sx={{ fontSize: 10, color: isLatest ? "#fff" : "var(--text-2)" }}>
                      v{c.versionNumber ?? "?"}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" fontFamily="Poppins" fontWeight={600} color="var(--text-1)" noWrap display="block">
                      {c.message || "Checkpoint"}
                    </Typography>
                    <Typography variant="caption" color="var(--text-2)" fontFamily="Poppins" sx={{ fontSize: 10 }}>
                      {moment(c.createdAt).format("DD MMM, HH:mm")} · {c.eventCount} events
                      {c.diffSummary && (c.diffSummary.added || c.diffSummary.removed) ? (
                        <>
                          {" · "}
                          <span style={{ color: "#16a34a" }}>+{c.diffSummary.added}</span>{" "}
                          <span style={{ color: "#dc2626" }}>−{c.diffSummary.removed}</span>
                        </>
                      ) : null}
                    </Typography>
                    <CommitClientLine commit={c} />
                  </Box>
                  {(c.pushed || readOnly) && (
                    <Tooltip title="Shared to cloud">
                      <CloudUpload sx={{ fontSize: 13, color: "#22c55e" }} />
                    </Tooltip>
                  )}
                  {(() => {
                    const sel = compareKeys.indexOf(commitKey(c)); // -1, 0, or 1
                    const isSel = sel >= 0;
                    const full = compareKeys.length === 2;
                    const disabled = full && !isSel;
                    return (
                      <Tooltip
                        title={
                          isSel ? "Selected — click to deselect"
                          : disabled ? "Clear a selection first (max 2)"
                          : "Compare: select this version"
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => toggleCompare(c)}
                            disabled={disabled}
                            sx={{ color: isSel ? "var(--primary-1)" : "var(--text-2)" }}
                          >
                            <Badge
                              badgeContent={isSel ? (sel === 0 ? "A" : "B") : 0}
                              color="primary"
                              overlap="circular"
                              sx={{ "& .MuiBadge-badge": { fontSize: 8, height: 14, minWidth: 14 } }}
                            >
                              <CompareArrows sx={{ fontSize: 16 }} />
                            </Badge>
                          </IconButton>
                        </span>
                      </Tooltip>
                    );
                  })()}
                  <Tooltip title="Preview this version's string">
                    <IconButton size="small" onClick={() => openPreview(c)}>
                      <Visibility sx={{ fontSize: 16, color: "var(--text-2)" }} />
                    </IconButton>
                  </Tooltip>
                  {!readOnly && (
                    <Tooltip title={isLatest ? "This is your current version" : "Restore this version (you'll review the changes first)"}>
                      <span>
                        <IconButton size="small" onClick={() => openRestore(c)} disabled={isLatest}>
                          <Restore sx={{ fontSize: 16, color: "var(--text-2)" }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Stack>
              );
            })}
          </Stack>
        )}
      </Box>

      {/* Save-version dialog */}
      <Dialog open={commitOpen} onClose={() => !busy && setCommitOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 15 }}>Save version</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Message"
            placeholder="e.g. First half tagged"
            value={commitMsg}
            onChange={(e) => setCommitMsg(e.target.value)}
            sx={{ mt: 1 }}
            inputProps={{ style: { fontFamily: "Poppins" } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCommitOpen(false)} disabled={busy === "commit"} sx={{ textTransform: "none", fontFamily: "Poppins" }}>
            Cancel
          </Button>
          <Button
            onClick={handleCommit}
            variant="contained"
            disabled={busy === "commit"}
            startIcon={busy === "commit" ? <CircularProgress size={13} color="inherit" /> : <Save sx={{ fontSize: 15 }} />}
            sx={{ textTransform: "none", fontFamily: "Poppins" }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Review & approve dialog */}
      {preview && (
        <MergeReviewDialog preview={preview} onClose={() => setPreview(null)} onMerged={handleMerged} />
      )}

      {/* Version preview dialog */}
      <Dialog
        open={Boolean(previewCommit)}
        onClose={() => setPreviewCommit(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { backgroundColor: "var(--surface-1)", color: "var(--text-1)", border: "1px solid var(--border-1)" } }}
      >
        {previewCommit && (
          <>
            <DialogTitle sx={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 15, borderBottom: "1px solid var(--border-1)" }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Visibility sx={{ fontSize: 17, color: "var(--text-2)" }} />
                <Box>
                  <Typography variant="subtitle1" fontFamily="Poppins" fontWeight={700} color="var(--text-1)">
                    {previewCommit.commit.isCurrent
                      ? "Current String"
                      : `v${previewCommit.commit.versionNumber ?? "?"} · ${previewCommit.commit.message || "Checkpoint"}`}
                  </Typography>
                  <Typography variant="caption" color="var(--text-2)" fontFamily="Poppins">
                    {previewCommit.commit.isCurrent
                      ? `Live working state · ${previewCommit.commit.eventCount} events`
                      : `${moment(previewCommit.commit.createdAt).format("DD MMM YYYY, HH:mm:ss")} · ${previewCommit.commit.eventCount} events`}
                  </Typography>
                </Box>
              </Stack>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
              <Box sx={{ p: 2.5, borderBottom: "1px solid var(--border-1)" }}>
                <Typography variant="overline" sx={{ color: "var(--text-2)", display: "block", mb: 0.75, fontSize: 10 }}>
                  Raw string
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    borderRadius: 1, p: 2, overflow: "auto", maxHeight: 160, m: 0,
                    fontSize: 12, lineHeight: 1.6, wordBreak: "break-all", whiteSpace: "pre-wrap",
                    backgroundColor: "var(--surface-2, rgba(0,0,0,0.04))", fontFamily: "monospace",
                  }}
                >
                  {previewCommit.string || "(empty)"}
                </Box>
              </Box>
              <Box sx={{ p: 2.5 }}>
                {(() => {
                  const tokens = parseEventTokens(previewCommit.string);
                  return (
                    <>
                      <Typography variant="overline" sx={{ color: "var(--text-2)", display: "block", mb: 1, fontSize: 10 }}>
                        Parsed events ({tokens.length})
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxHeight: 230, overflowY: "auto" }}>
                        {tokens.length === 0 ? (
                          <Typography variant="body2" color="var(--text-2)" fontFamily="Poppins">
                            No meaningful events in this version.
                          </Typography>
                        ) : (
                          tokens.map((t, i) => (
                            <Chip key={i} label={`${i + 1}. ${t}`} size="small" variant="outlined" sx={{ height: 22, fontFamily: "monospace", fontSize: 11,color: "var(--text-2)", "& .MuiChip-label": { color: "var(--text-2)"} }} />
                          ))
                        )}
                      </Box>
                    </>
                  );
                })()}
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid var(--border-1)" }}>
              <Button onClick={() => setPreviewCommit(null)} variant="outlined" size="small" sx={{ textTransform: "none", fontFamily: "Poppins", borderColor: "var(--border-1)", color: "var(--text-1)" }}>
                Close
              </Button>
              {!readOnly && !previewCommit.commit.isCurrent && commitKey(previewCommit.commit) !== latestCommitKey && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Restore sx={{ fontSize: 15 }} />}
                  onClick={() => {
                    const c = previewCommit.commit;
                    setPreviewCommit(null);
                    openRestore(c);
                  }}
                  sx={{ textTransform: "none", fontFamily: "Poppins" }}
                >
                  Restore this version
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Diff dialog — compare two selected commits */}
      <Dialog
        open={Boolean(diffResult)}
        onClose={clearCompare}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { backgroundColor: "var(--surface-1)", color: "var(--text-1)", border: "1px solid var(--border-1)" } }}
      >
        {diffResult && (
          <>
            <DialogTitle sx={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 15, borderBottom: "1px solid var(--border-1)" }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <CompareArrows sx={{ fontSize: 17, color: "var(--text-2)" }} />
                <Box>
                  <Typography variant="subtitle1" fontFamily="Poppins" fontWeight={700} color="var(--text-1)">
                    v{diffResult.base.versionNumber ?? "?"} → v{diffResult.target.versionNumber ?? "?"}
                  </Typography>
                  <Typography variant="caption" color="var(--text-2)" fontFamily="Poppins">
                    {moment(diffResult.base.createdAt).format("DD MMM, HH:mm")} → {moment(diffResult.target.createdAt).format("DD MMM, HH:mm")}
                    {" · "}
                    <span style={{ color: "#16a34a" }}>+{diffResult.diff.addedEvents.length}</span>{" "}
                    <span style={{ color: "#dc2626" }}>−{diffResult.diff.removedEvents.length}</span>
                  </Typography>
                </Box>
              </Stack>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
              {!diffResult.diff.hasChange ? (
                <Box sx={{ p: 3 }}>
                  <Typography variant="body2" color="var(--text-2)" fontFamily="Poppins">
                    These two versions have identical event strings.
                  </Typography>
                </Box>
              ) : (
                <Stack direction={{ xs: "column", sm: "row" }} divider={<Divider flexItem orientation="vertical" sx={{ borderColor: "var(--border-1)" }} />}>
                  <Box sx={{ flex: 1, p: 2.5 }}>
                    <Typography variant="overline" sx={{ color: "#16a34a", display: "block", mb: 1, fontSize: 10 }}>
                      Added ({diffResult.diff.addedEvents.length})
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxHeight: 300, overflowY: "auto" }}>
                      {diffResult.diff.addedEvents.length === 0 ? (
                        <Typography variant="caption" color="var(--text-2)" fontFamily="Poppins">None</Typography>
                      ) : (
                        diffResult.diff.addedEvents.map((t, i) => (
                          <Chip key={i} label={t} size="small" variant="outlined"
                            sx={{ height: 22, fontFamily: "monospace", fontSize: 11, color: "#16a34a", borderColor: "#16a34a55" }} />
                        ))
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ flex: 1, p: 2.5 }}>
                    <Typography variant="overline" sx={{ color: "#dc2626", display: "block", mb: 1, fontSize: 10 }}>
                      Removed ({diffResult.diff.removedEvents.length})
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxHeight: 300, overflowY: "auto" }}>
                      {diffResult.diff.removedEvents.length === 0 ? (
                        <Typography variant="caption" color="var(--text-2)" fontFamily="Poppins">None</Typography>
                      ) : (
                        diffResult.diff.removedEvents.map((t, i) => (
                          <Chip key={i} label={t} size="small" variant="outlined"
                            sx={{ height: 22, fontFamily: "monospace", fontSize: 11, color: "#dc2626", borderColor: "#dc262655" }} />
                        ))
                      )}
                    </Box>
                  </Box>
                </Stack>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid var(--border-1)" }}>
              <Button onClick={clearCompare} variant="outlined" size="small" sx={{ textTransform: "none", fontFamily: "Poppins", borderColor: "var(--border-1)", color: "var(--text-1)" }}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Paper>
  );
};

const vcsBtnSx = {
  textTransform: "none",
  fontFamily: "Poppins",
  fontSize: 12,
  py: 0.3,
  borderColor: "var(--border-1)",
  color: "var(--text-1)",
};

export default BranchVcsPanel;

// Which device produced a commit — shown so cross-device overwrites can be traced.
function CommitClientLine({ commit }) {
  const client = commit.client;
  if (!client && !commit.headConflict) return null;
  const summary = client
    ? [client.platform, client.screen, client.deviceId].filter(Boolean).join(" · ")
    : "";
  const details = client
    ? [
        `Device: ${client.deviceId || "?"}`,
        `Platform: ${client.platform || "?"}`,
        `Screen: ${client.screen || "?"}`,
        `Language: ${client.language || "?"} · ${client.timeZone || "?"}`,
        `User agent: ${client.userAgent || "?"}`,
      ].join("\n")
    : "";
  return (
    <Tooltip title={<span style={{ whiteSpace: "pre-line" }}>{details}</span>} disableHoverListener={!client}>
      <Typography
        variant="caption"
        color="var(--text-2)"
        fontFamily="Poppins"
        noWrap
        display="block"
        sx={{ fontSize: 10 }}
      >
        {summary}
        {commit.headConflict && (
          <span style={{ color: "#d97706" }}>
            {summary ? " · " : ""}cloud copy not updated (another device)
          </span>
        )}
      </Typography>
    </Tooltip>
  );
}
